<template>
  <div ref="mapContainer" class="map-view"></div>
</template>

<script>
import { createMap } from './maplibre-init.js'

export default {
  name: 'MapView',

  props: {
    styleId: { type: String, default: 'dark' },
    center: { type: Array, default: function() { return [-99.1332, 19.4326] } },
    zoom: { type: Number, default: 12 },
  },

  data: function() {
    return {
      map: null,
    }
  },

  created: function() {
    this._resizeObserver = null
    this._resizeRAF = null
  },

  mounted: function() {
    var apiKey = import.meta.env.VITE_MAPTILER_KEY
    var tilesBase = import.meta.env.VITE_TILES_BASE_URL
    // Con self-host (tilesBase seteado) la key solo hace falta para satélite,
    // así que NO bloqueamos el init. Solo abortamos si no hay NI key NI
    // tiles self-hosted (no habría de dónde sacar basemap).
    if (!apiKey && !tilesBase) {
      console.error('[MapView] Falta VITE_MAPTILER_KEY (o VITE_TILES_BASE_URL para self-host)')
      return
    }
    if (!apiKey) {
      console.warn('[MapView] VITE_MAPTILER_KEY ausente: el modo satélite no estará disponible')
    }

    var self = this
    this.map = createMap(this.$refs.mapContainer, {
      styleId: this.styleId,
      center: this.center,
      zoom: this.zoom,
      apiKey: apiKey,
    })

    this._resizeObserver = new ResizeObserver(function() {
      if (self._resizeRAF) return
      self._resizeRAF = requestAnimationFrame(function() {
        if (self.map) self.map.resize()
        self._resizeRAF = null
      })
    })
    this._resizeObserver.observe(this.$el)

    // Nota: `map.on('load')` a veces no se dispara incluso cuando el style
    // ya se cargo (edge case con HMR / reconexion vite). Escuchamos tanto
    // 'load' como 'style.load' y ademas un fallback con setTimeout que
    // verifica `isStyleLoaded()` cada 500ms, para que map-ready siempre
    // llegue al componente padre.
    this._readyEmitted = false
    var emitReady = function() {
      if (self._readyEmitted) return
      self._readyEmitted = true
      if (self._readyInterval) { clearInterval(self._readyInterval); self._readyInterval = null }
      if (self._readyHardTimeout) { clearTimeout(self._readyHardTimeout); self._readyHardTimeout = null }
      self.$emit('map-ready', self.map)
    }
    this.map.on('load', emitReady)
    this.map.on('style.load', function() {
      if (self.map && self.map.isStyleLoaded()) emitReady()
    })
    this._readyInterval = setInterval(function() {
      if (self._readyEmitted) { return }
      if (self.map && self.map.isStyleLoaded && self.map.isStyleLoaded()) {
        emitReady()
      }
    }, 500)
    // Hard safety: si tras 10s el map no emitio ready, forzarlo igual para
    // no dejar la UI colgada en "Iniciando mapa...".
    this._readyHardTimeout = setTimeout(function() {
      if (!self._readyEmitted && self.map) {
        emitReady()
      }
    }, 10000)

    this.map.on('moveend', function() {
      self.$emit('viewport-change', {
        center: self.map.getCenter().toArray(),
        zoom: self.map.getZoom(),
        bounds: self.map.getBounds(),
      })
    })
  },

  beforeDestroy: function() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    if (this._resizeRAF) {
      cancelAnimationFrame(this._resizeRAF)
      this._resizeRAF = null
    }
    // Limpiar timers del fallback de map-ready para evitar leaks en HMR.
    if (this._readyInterval) { clearInterval(this._readyInterval); this._readyInterval = null }
    if (this._readyHardTimeout) { clearTimeout(this._readyHardTimeout); this._readyHardTimeout = null }
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },

  methods: {
    getMap: function() {
      return this.map
    },
  },
}
</script>

<style scoped>
.map-view {
  width: 100%;
  height: 100%;
}
</style>
